import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Eye, Terminal, Globe, Lock, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PublishModal({ mediaUrl, mediaType, prompt, style, aspectRatio, token, onClose, onPublished }) {
  const [caption, setCaption] = useState(prompt || '');
  const [tagsInput, setTagsInput] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const parsedTags = tagsInput.split(/[,\s]+/).filter(t=>t.length>0&&t.length<=30).slice(0,10);

  const handlePublish = async () => {
    if (!token) { setError('AUTH REQUIRED'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/content/posts`, {
        method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},
        body: JSON.stringify({media_url:mediaUrl,media_type:mediaType||'image',prompt:caption,tags:parsedTags,is_public:isPublic,style,aspect_ratio:aspectRatio}),
      });
      if (!r.ok) { const d=await r.json(); throw new Error(d.detail||'ERROR'); }
      onPublished?.();
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 font-manrope">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)' }}/>
        <motion.div initial={{scale:0.95, y:20, opacity:0}} animate={{scale:1, y:0, opacity:1}} exit={{scale:0.95, y:20, opacity:0}} className="w-full max-w-xl bg-zinc-950 border border-emerald-500/20 rounded-lg overflow-hidden relative shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/60 bg-zinc-900/50">
            <h3 className="font-black font-chivo text-white tracking-widest uppercase flex items-center gap-2">
              <Terminal className="w-4 h-4 text-emerald-500"/> PUBLISH PAYLOAD
            </h3>
            <button onClick={onClose} className="text-zinc-500 hover:text-emerald-400 transition-colors"><X className="w-5 h-5"/></button>
          </div>
          <div className="p-6 space-y-6">
            <div className="aspect-video rounded border border-zinc-800 bg-black flex items-center justify-center relative overflow-hidden">
              {mediaType==='video' ? <video src={mediaUrl} className="w-full h-full object-contain" muted loop autoPlay playsInline/> : <img src={mediaUrl} alt="preview" className="w-full h-full object-contain"/>}
              <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/80 border border-zinc-800 rounded text-[10px] font-mono text-emerald-500 uppercase tracking-widest">{mediaType} PREVIEW</div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-2 uppercase tracking-widest">PROMPT METADATA</label>
                <textarea value={caption} onChange={e=>setCaption(e.target.value)} rows={2} maxLength={300} placeholder="System prompt details..." className="w-full bg-black border border-zinc-800 focus:border-emerald-500/50 rounded-lg px-4 py-3 text-zinc-300 text-sm font-mono outline-none resize-none transition-colors placeholder:text-zinc-700"/>
              </div>
              <div>
                <label className="block text-xs font-mono text-zinc-500 mb-2 uppercase tracking-widest">TAGS</label>
                <input value={tagsInput} onChange={e=>setTagsInput(e.target.value)} placeholder="ai, scan, veritas..." className="w-full bg-black border border-zinc-800 focus:border-emerald-500/50 rounded-lg px-4 py-3 text-zinc-300 text-sm font-mono outline-none transition-colors placeholder:text-zinc-700"/>
                {parsedTags.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{parsedTags.map(t=><span key={t} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[10px] font-mono tracking-wider">#{t.toUpperCase()}</span>)}</div>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button onClick={()=>setIsPublic(true)} className={`flex items-center justify-center gap-2 py-3 rounded-lg border text-xs font-chivo font-bold uppercase transition-all ${isPublic ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
                <Globe className="w-4 h-4"/> Global Node
              </button>
              <button onClick={()=>setIsPublic(false)} className={`flex items-center justify-center gap-2 py-3 rounded-lg border text-xs font-chivo font-bold uppercase transition-all ${!isPublic ? 'bg-zinc-800 border-zinc-700 text-zinc-300' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
                <Lock className="w-4 h-4"/> Isolated Node
              </button>
            </div>
            
            {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs font-mono uppercase tracking-wider flex items-center gap-2"><Shield className="w-4 h-4"/> {error}</div>}
            
            <button onClick={handlePublish} disabled={loading} className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-chivo font-black uppercase tracking-widest rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <span className="animate-pulse">TRANSMITTING...</span> : 'EXECUTE PUBLISH'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function VeritasContent() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems([
      { id: 1, type: 'video', model: 'VERITAS-GEN-1', confidence: 98.4, prompt: 'Ciudad cyberpunk bajo la lluvia, hiperrealista, 8k' },
      { id: 2, type: 'image', model: 'VERITAS-GEN-2', confidence: 99.1, prompt: 'Retrato fotorrealista de un astronauta en Marte' },
      { id: 3, type: 'video', model: 'VERITAS-GEN-1', confidence: 97.8, prompt: 'Vista aérea de un océano bioluminiscente de noche' }
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300 font-manrope flex flex-col">
      <header className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 w-full">
          <button onClick={() => window.location.href='/'} className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <Globe className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-500" />
            <span className="font-heading text-lg sm:text-xl font-black tracking-tight font-chivo text-white">
              VERITAS <span className="text-emerald-500">CONTENT</span>
            </span>
          </button>
          <div className="flex items-center gap-2 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest">
            <Terminal className="w-3 h-3 text-emerald-500" /> GLOBAL NODE
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map(item => (
            <motion.div key={item.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden relative group hover:border-emerald-500/50 transition-colors shadow-xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="aspect-[9/16] bg-black flex items-center justify-center relative overflow-hidden">
                <span className="text-zinc-700 font-chivo text-2xl font-black opacity-30 tracking-widest uppercase">[{item.type} PREVIEW]</span>
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent pointer-events-none" />
              </div>
              <div className="p-5 bg-zinc-950 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-mono font-bold text-emerald-500 uppercase tracking-widest">MODEL: {item.model}</span>
                  <span className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest">CONF: {item.confidence}%</span>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed line-clamp-2"><span className="text-white font-semibold">PROMPT: </span>{item.prompt}</p>
                <div className="mt-4 pt-4 border-t border-zinc-800/60 flex justify-between gap-4">
                  <button className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"><Eye className="w-4 h-4" /> <span className="text-xs font-bold font-chivo uppercase">Inspect</span></button>
                  <button className="flex items-center gap-2 text-zinc-500 hover:text-emerald-400 transition-colors"><Shield className="w-4 h-4" /> <span className="text-xs font-bold font-chivo uppercase">Verify</span></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}